import React, { FC, useState } from 'react';

import { Box, Icon, Text, Link } from '@lucid/components-react';
import { Drupal_NodeAwards } from 'generated/drupal-graphql';
import { mqMin, LucidTheme } from '@lucid/theme';

interface AwardsProps {
  articles: Drupal_NodeAwards[];
}

const Awards: FC<AwardsProps> = ({ articles }) => {
  const itemsPerRow = 4;
  const [itemsToShow, setItemsToShow] = useState(4);
  const twoThroughRest = articles.slice(0, articles.length);
  const articlesBelow = twoThroughRest.slice(0, itemsToShow);

  return (
    <Box
      css={(theme: LucidTheme) => ({
        label: 'awards',
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gridTemplateRows: 'repeat(1, 1fr)',
        gridGap: theme.space[4],
        marginBottom: theme.space[10]
      })}
    >
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gridColumn: '1 / span 12'
        }}
      >
        {articlesBelow.map(
          ({ title, fieldPublisher, fieldSourceUrl, fieldImageLink }) => (
            <Box
              key={title}
              css={(theme: LucidTheme) => ({
                borderBottom: `1px solid ${theme.colors.coolGrey[20]}`,
                marginBottom: theme.space[4],
                flex: '0 1 100%',
                maxWidth: '100%',
                [mqMin.tabletLandscape]: {
                  border: 'none',
                  flex: '0 1 22%',
                  maxWidth: '22%',
                  padding: theme.space[4],
                  paddingLeft: 0,
                  margin: `${theme.space[5]}px 0`
                }
              })}
            >
              <Box>
                <img
                  alt={title!}
                  css={(theme: LucidTheme) => ({
                    height: '180px',
                    marginBottom: theme.space[3]
                  })}
                  loading="lazy"
                  src={fieldImageLink!}
                />
              </Box>
              <Text
                css={(theme: LucidTheme) => ({
                  margin: 0,
                  color: theme.colors.blue[60]
                })}
                variant="COformXS400"
              >
                {fieldPublisher}
              </Text>
              <Link to={fieldSourceUrl!}>
                <Text
                  css={{
                    width: '100%',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: 'vertical'
                  }}
                  variant="COsubheadXXS700"
                >
                  {title}
                </Text>
              </Link>
            </Box>
          )
        )}
      </Box>
      <Box css={{ textAlign: 'center', gridColumn: '1 / span 12' }}>
        {twoThroughRest.length > itemsToShow && (
          <Box
            css={() => ({
              display: 'flex',
              flexWrap: 'wrap',
              '&:hover': { cursor: 'pointer' }
            })}
            onClick={() => setItemsToShow(itemsToShow + itemsPerRow)}
          >
            <Text
              css={(theme: LucidTheme) => ({
                color: theme.colors.primary,
                flex: '0 1 100%',
                width: '100%',
                '&:hover': { cursor: 'pointer' }
              })}
              variant="COsubheadXXS700"
            >
              Show more
            </Text>
            <Text
              css={() => ({
                flex: '0 1 100%',
                width: '100%',
                '&:hover': { cursor: 'pointer' }
              })}
              variant="COsubheadXXS700"
            >
              <Icon type="CarotDown" />
            </Text>
          </Box>
        )}
        {twoThroughRest.length < itemsToShow && twoThroughRest.length > itemsPerRow && (
          <Box
            css={() => ({
              display: 'flex',
              flexWrap: 'wrap',
              '&:hover': { cursor: 'pointer' }
            })}
            onClick={() => setItemsToShow(itemsPerRow)}
          >
            <Text
              css={(theme: LucidTheme) => ({
                color: theme.colors.primary,
                flex: '0 1 100%',
                width: '100%',
                '&:hover': { cursor: 'pointer' }
              })}
              variant="COsubheadXXS700"
            >
              <Icon
                css={{
                  transform: 'rotate(180deg)'
                }}
                type="CarotDown"
              />
              <Box
                css={(theme: LucidTheme) => ({
                  marginTop: theme.space[3]
                })}
              >
                Show less
              </Box>
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Awards;
